import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { isCustomer } from '../../../api';
import { trackEvent } from '../../../analytics';
import { HangoutOffer, HangoutPerson, HangoutVenue, InviteType } from '../types';
import { formatInviteDate, formatInviteTime, INVITE_TYPES, loginUrlFor } from '../utils';
import { Avatar, VenueImage } from './common';

// ---------- Venue summary card (Create Invite, screen 2) ----------

export function VenueInviteCard({ venue, offer }: { venue: HangoutVenue; offer?: HangoutOffer | null }) {
  return (
    <div className="hg-card hg-venue">
      <VenueImage url={venue.imageUrl} alt={venue.name} />
      <div style={{ minWidth: 0 }}>
        <h3>{venue.name}</h3>
        <p>{[venue.area, venue.city].filter(Boolean).join(', ')}</p>
        {offer && (
          <div className="hg-offer">
            {offer.title}{offer.benefit ? ` · ${offer.benefit}` : ''}
            {offer.validity && <small>{offer.validity}</small>}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Invite type (three cards; the chosen one turns gold) ----------

const TYPE_ICON: Record<InviteType, React.ReactNode> = {
  JustMyFriends: <PersonOutlineIcon />,
  FriendsOfFriends: <GroupsOutlinedIcon />,
  PrivateGroup: <LockOutlinedIcon />
};

export function InviteTypeSelector({ value, onChange }: { value: InviteType | ''; onChange: (t: InviteType) => void }) {
  return (
    <div className="hg-types" role="radiogroup" aria-label="Invite type">
      {INVITE_TYPES.map((t) => (
        <button
          key={t.value}
          type="button"
          role="radio"
          aria-checked={value === t.value}
          className={`hg-type ${value === t.value ? 'on' : ''}`}
          onClick={() => onChange(t.value)}
        >
          {TYPE_ICON[t.value]}
          <span><b>{t.title}</b><small>{t.description}</small></span>
          <span className="hg-radio" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

// ---------- The invitation (screen 3; compact version inside the share sheet) ----------

export interface InvitePreviewData {
  venue: HangoutVenue;
  offer?: HangoutOffer | null;
  inviteDateTime: string;
  host: HangoutPerson;
  message?: string | null;
}

export function InvitePreviewCard({ invite, compact, children }: { invite: InvitePreviewData; compact?: boolean; children?: React.ReactNode }) {
  return (
    <article className={`hg-invite ${compact ? 'compact' : ''}`} aria-label={`Invitation to ${invite.venue.name}`}>
      <div className="hg-invite-brand">
        {/* The logo image already carries the "Royal People Royal Choice" tagline. */}
        <img src="/logo2.png" alt="Cheers2Night — Royal People Royal Choice" />
      </div>
      <VenueImage url={invite.venue.imageUrl} alt={invite.venue.name} className="hg-invite-photo" />
      <div className="hg-invite-body">
        <p className="hg-invited">You're Invited! 🎉</p>
        <h2>Night Out at<br />{invite.venue.name}</h2>
        <div className="hg-meta"><CalendarMonthOutlinedIcon />{formatInviteDate(invite.inviteDateTime)} &nbsp;|&nbsp; {formatInviteTime(invite.inviteDateTime)}</div>
        <div className="hg-meta"><LocationOnOutlinedIcon />{[invite.venue.area, invite.venue.city].filter(Boolean).join(', ')}</div>
        {invite.offer && <div className="hg-offer-chip"><LocalOfferOutlinedIcon fontSize="small" />{invite.offer.title}</div>}
        <div className="hg-inviter">
          <Avatar initials={invite.host.initials} small={compact} />
          <div><small>Invited by</small><b>{invite.host.displayName}</b></div>
        </div>
        {invite.message && !compact && <p className="hg-invite-msg">{invite.message}</p>}
        {children}
      </div>
    </article>
  );
}

// ---------- Copy link ----------

export function CopyInviteLinkButton({ url, label = 'Copy invite link' }: { url: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Older browsers / insecure contexts: fall back to a hidden textarea.
      const t = document.createElement('textarea');
      t.value = url;
      document.body.appendChild(t);
      t.select();
      document.execCommand('copy');
      t.remove();
    }
    trackEvent('hangout_invite_link_copied');
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button type="button" className="hg-icon-btn" onClick={copy} aria-label={copied ? 'Link copied' : label} title={copied ? 'Copied' : label}>
      {copied ? <CheckIcon style={{ color: '#4ade80' }} /> : <ContentCopyIcon />}
    </button>
  );
}

// ---------- Venue page CTA ----------

// "Invite to Hangout" on the venue page. Signed-out visitors log in first and come straight back.
export function InviteToHangoutButton({ venueId, style }: { venueId: number; style?: React.CSSProperties }) {
  const navigate = useNavigate();
  const target = `/venues/${venueId}/invite`;
  return (
    <Link
      className="hg-cta"
      style={style}
      to={target}
      onClick={(e) => {
        trackEvent('hangout_invite_started', { venue_id: venueId });
        if (!isCustomer()) {
          e.preventDefault();
          navigate(loginUrlFor(target));
        }
      }}
    >
      <GroupsOutlinedIcon /> Invite to Hangout
    </Link>
  );
}
